import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Tab,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Tabs,
  Typography,
  Checkbox,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import {
  AllStructuresForUserManagementDocument,
  AllStructuresForUserManagementQuery,
  useRemoveUserGroupsFromStructureMutation,
} from '@/__generated__/graphql';
import { defaultPaginationSize, FALLBACK_INSPECTION_IMAGE } from '@/constants';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { AddUserGroupsDialog } from '../Dialogs/AddUserGroupsDialog';
import { StyledTableCell, TablePaginationContainer } from '../styles';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { textButtonStyle } from '@/theme';
import { TablePagination } from '../../shared/TablePagination';

type Props = {
  platform: AllStructuresForUserManagementQuery['allStructures'][0] | undefined;
};

type TabLabel = 'groups';

export const PlatformDetails = ({ platform }: Props) => {
  const [selectedTab, setSelectedTab] = useState<TabLabel>('groups');
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [addUserGroupsModal, setAddUserGroupsModal] = useState(false);
  const [removeAlert, setRemoveAlert] = useState(false);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [removeUserGroupsFromStructure] = useRemoveUserGroupsFromStructureMutation();

  const { platformName, platformId, imagePath, userGroups, groupsIds } = useMemo(() => {
    setSelectedRows([]);
    const platformUserGroups = platform?.userGroups || [];
    if (platformUserGroups.length > 0) {
      setRowsPerPage(platformUserGroups.length);
    }
    return {
      imagePath: platform?.coverImagePath ?? FALLBACK_INSPECTION_IMAGE,
      userGroups: platformUserGroups,
      groupsIds: platformUserGroups.map((userGroup) => userGroup.id),
      platformName: platform?.name || '',
      platformId: platform?.id || '',
    };
  }, [platform]);

  const paginatedUserGroups = useMemo(() => {
    const pageCount = Math.ceil(userGroups.length / rowsPerPage);
    if (page >= pageCount && pageCount > 0) {
      setPage(pageCount - 1);
    }
    return userGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [page, rowsPerPage, userGroups]);

  const handleChangePage = (newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    const pageCount = Math.ceil(userGroups.length / newRowsPerPage);
    if (page > pageCount - 1) setPage(0);
  };

  const handleTabChange = useCallback(
    (newValue: TabLabel) => {
      setSelectedTab(newValue);
    },
    [setSelectedTab]
  );

  const handleToggleAddUserGroupsModal = useCallback(() => {
    setAddUserGroupsModal((previous) => !previous);
  }, []);

  const handleSelectRow = useCallback(
    (id: string) => {
      setSelectedRows((previousRows) =>
        previousRows.includes(id)
          ? previousRows.filter((rowId) => rowId !== id)
          : [...previousRows, id]
      );
    },
    [setSelectedRows]
  );

  const handleSelectAllRows = useCallback(() => {
    setSelectedRows((previousRows) => (previousRows.length === userGroups.length ? [] : groupsIds));
  }, [groupsIds, userGroups.length]);

  const handleRemoveUserFromGroup = useCallback(async () => {
    removeUserGroupsFromStructure({
      variables: {
        input: {
          structureIds: [platformId],
          userGroupIds: selectedRows,
        },
      },
      refetchQueries: [AllStructuresForUserManagementDocument],
      onCompleted: () => {
        showSnackBar(`Removing platform form this group successfully`, 'success');
        setRemoveAlert(false);
        setSelectedRows([]);
      },
    });
  }, [removeUserGroupsFromStructure, platformId, selectedRows, showSnackBar]);

  const handleRemoveGroups = useCallback((groupId?: string) => {
    if (groupId) {
      setSelectedRows([groupId]);
    }
    setRemoveAlert(true);
  }, []);

  if (!platform) return <></>;

  return (
    <>
      <Stack spacing={2} direction="row">
        <Box>
          <img
            alt={platformName}
            src={getCloudfrontUrl(imagePath)}
            crossOrigin="use-credentials"
            onError={(event) => {
              const { currentTarget } = event;
              currentTarget.src = FALLBACK_INSPECTION_IMAGE;
            }}
            width="180"
            height="120"
            style={{ borderRadius: '15px' }}
          />
        </Box>
        <Box flex={1} sx={{ pt: 1 }}>
          <Typography variant="h6">{platformName}</Typography>
          <Typography variant="body1">{`${userGroups.length} group(s)`}</Typography>
        </Box>
        <Button
          variant="text"
          size="medium"
          data-testid="add-user-button"
          startIcon={<AddCircleRoundedIcon />}
          onClick={handleToggleAddUserGroupsModal}
        >
          Add to User Groups
        </Button>
      </Stack>
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Tabs sx={{ my: 2 }} value={selectedTab} onChange={(_, value) => handleTabChange(value)}>
            <Tab label="Group" value="groups" />
          </Tabs>
          {selectedRows.length > 0 && (
            <>
              <Button
                sx={{ height: 40 }}
                variant="contained"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={() => handleRemoveGroups()}
              >
                Remove {selectedRows.length} Selected Groups
              </Button>
            </>
          )}
        </Stack>
        {selectedTab === 'groups' && (
          <>
            <TableContainer sx={{ overflowY: 'auto', maxHeight: 'calc(39vh / var(--zoom))' }}>
              <Table>
                <TableBody>
                  {userGroups.length > 0 ? (
                    <TableRow>
                      <StyledTableCell sx={{ width: '100%', fontSize: '1.6rem', fontWeight: 600 }}>
                        <Checkbox
                          checked={selectedRows.length === userGroups.length}
                          indeterminate={
                            selectedRows.length > 0 && selectedRows.length < userGroups.length
                          }
                          onChange={handleSelectAllRows}
                        />
                        Name
                      </StyledTableCell>
                      <StyledTableCell sx={{ fontSize: '1.6rem', fontWeight: 600 }}>
                        Actions
                      </StyledTableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <Typography variant="h5" sx={{ p: 2 }}>
                        No Groups Assigned
                      </Typography>
                    </TableRow>
                  )}
                  {paginatedUserGroups?.map((userGroup) => (
                    <TableRow hover key={`group-${userGroup.id}`}>
                      <StyledTableCell sx={{ width: '100%' }}>
                        <Checkbox
                          checked={selectedRows.includes(userGroup.id)}
                          onChange={() => handleSelectRow(userGroup.id)}
                        />
                        {userGroup.name}
                      </StyledTableCell>
                      <StyledTableCell>
                        <Button
                          sx={textButtonStyle}
                          variant="text"
                          startIcon={<DeleteIcon />}
                          onClick={() => handleRemoveGroups(userGroup.id)}
                        >
                          Remove Access
                        </Button>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePaginationContainer>
              <TablePagination
                count={userGroups.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[
                  ...defaultPaginationSize,
                  { label: 'All', value: userGroups.length },
                ]}
              />
            </TablePaginationContainer>
          </>
        )}
        {removeAlert && (
          <ConfirmationDialog
            title={`Removing Group(s) from ${platformName}`}
            description={`Are you sure you want to remove
            ${selectedRows.length} group(s) from ${platformName}?
            User(s) of these groups will no longer have the access to ${platformName} platform.`}
            confirmButtonText="Remove Group(s)"
            cancelButtonText="Cancel"
            onConfirm={handleRemoveUserFromGroup}
            handleClose={() => setRemoveAlert(false)}
            isOpen={removeAlert}
          />
        )}
        {addUserGroupsModal && (
          <AddUserGroupsDialog
            open={addUserGroupsModal}
            structureIds={[platformId]}
            assignedUserGroups={userGroups}
            handleToggle={handleToggleAddUserGroupsModal}
          />
        )}
      </Box>
    </>
  );
};
